'use client';

import React from 'react';
import type { CustomBackground } from '@/hooks/useCustomBackgroundImage';
import { Mic, MicOff, VolumeX, Copy, Mail, ChevronDown, Plus, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PreJoinSettingsMenuProps {
  activeTab: 'audio' | 'video' | 'backgrounds';
  audioEnabled: boolean;
  setAudioEnabled: (enabled: boolean) => void;
  videoEnabled: boolean;
  setVideoEnabled: (enabled: boolean) => void;
  audioDeviceId: string;
  setAudioDeviceId: (deviceId: string) => void;
  videoDeviceId: string;
  setVideoDeviceId: (deviceId: string) => void;
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  audioOutputDeviceId: string;
  setAudioOutputDeviceId: (deviceId: string) => void;
  videoTrack?: any;
  backgroundType: 'none' | 'blur' | 'image';
  virtualBackgroundImagePath: string;
  selectBackground: (type: 'none' | 'blur' | 'image', imagePath?: string) => void;
  customBackgrounds: CustomBackground[];
  uploadCustomBackground: (
    file: File,
  ) => Promise<{ success: boolean; message?: string; dataUrl?: string }>;
  removeCustomBackground: (id: string) => void;
  isUploadingCustomBackground: boolean;
  customBackgroundError: string | null;
  resetCustomBackgroundError: () => void;
  audioLevel: number;
  isAnalyzingAudio: boolean;
  isPlayingTestSound: boolean;
  playTestSound: () => void;
  supportsSetSinkId: boolean;
}

export function PreJoinSettingsMenu({
  activeTab,
  audioEnabled,
  setAudioEnabled,
  videoEnabled,
  setVideoEnabled,
  audioDeviceId,
  setAudioDeviceId,
  videoDeviceId,
  setVideoDeviceId,
  audioInputDevices,
  videoInputDevices,
  audioOutputDevices,
  audioOutputDeviceId,
  setAudioOutputDeviceId,
  videoTrack,
  backgroundType,
  virtualBackgroundImagePath,
  selectBackground,
  customBackgrounds,
  uploadCustomBackground,
  removeCustomBackground,
  isUploadingCustomBackground,
  customBackgroundError,
  resetCustomBackgroundError,
  audioLevel,
  isAnalyzingAudio,
  isPlayingTestSound,
  playTestSound,
  supportsSetSinkId,
}: PreJoinSettingsMenuProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await uploadCustomBackground(file);
      event.target.value = '';
    },
    [uploadCustomBackground],
  );

  const handleOpenFileDialog = React.useCallback(() => {
    resetCustomBackgroundError();
    fileInputRef.current?.click();
  }, [resetCustomBackgroundError]);

  return (
    <div className="h-full">
      {activeTab === 'audio' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Áudio</h3>

          {/* Seção Microfone */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-3">Microfone</h4>
            <div className="space-y-3">
              <Select
                value={audioDeviceId || ''}
                onValueChange={(value) => setAudioDeviceId(value)}
              >
                <SelectTrigger className="w-full bg-neutral-800 border-neutral-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue placeholder="Selecione um microfone" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-800 border-neutral-600">
                  {audioInputDevices.map((device, index) => (
                    <SelectItem
                      key={device.deviceId}
                      value={device.deviceId}
                      className="text-white focus:bg-neutral-700 focus:text-white"
                    >
                      {device.label || `Microfone ${index + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Indicador de nível de áudio */}
            <div className="space-y-2">
              {audioEnabled && (
                <div className="space-y-2">
                  <div className="text-sm text-neutral-300">Nível do microfone:</div>
                  <div className="flex gap-1">
                    {Array.from({ length: 20 }, (_, i) => {
                      const isActive = audioLevel > i / 20;
                      const isLow = i < 7;
                      const isMedium = i >= 7 && i < 14;
                      const isHigh = i >= 14;

                      let colorClass = 'bg-neutral-600';
                      if (isActive) {
                        if (isLow) colorClass = 'bg-green-500';
                        else if (isMedium) colorClass = 'bg-yellow-500';
                        else if (isHigh) colorClass = 'bg-red-500';
                      }

                      return (
                        <div
                          key={i}
                          className={`h-2 w-3 rounded-sm transition-colors duration-150 ${colorClass}`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Seção Saída de áudio */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-3">Saída de áudio</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Select
                  value={audioOutputDeviceId || 'default'}
                  onValueChange={(value) => setAudioOutputDeviceId(value)}
                >
                  <SelectTrigger className="flex-1 bg-neutral-800 border-neutral-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <SelectValue placeholder="Selecione um dispositivo de áudio" />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-800 border-neutral-600">
                    <SelectItem
                      value="default"
                      className="text-white focus:bg-neutral-700 focus:text-white"
                    >
                      Sistema padrão
                    </SelectItem>
                    {audioOutputDevices.map((device, index) => (
                      <SelectItem
                        key={device.deviceId}
                        value={device.deviceId}
                        className="text-white focus:bg-neutral-700 focus:text-white"
                      >
                        {device.label || `Alto-falante ${index + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Teste de áudio */}
            <div className="space-y-3">
              <Button
                onClick={playTestSound}
                disabled={isPlayingTestSound}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isPlayingTestSound ? 'Testando...' : 'Testar áudio'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'video' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Vídeo</h3>

          {/* Preview da câmera */}
          {videoTrack && (
            <div className="mb-6">
              <div className="relative w-full h-64 bg-neutral-900 rounded-lg overflow-hidden border border-neutral-700">
                <video
                  className="w-full h-full object-cover"
                  ref={(el) => {
                    if (el && videoTrack) {
                      videoTrack.attach(el);
                      // Não aplicar processadores nos previews dos modais para evitar conflitos
                    }
                  }}
                  data-preview-mode="true"
                />
              </div>
            </div>
          )}

          {/* Configurações de Câmera */}
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="text-sm text-neutral-300">Dispositivo de vídeo:</label>
              <Select
                value={videoDeviceId || ''}
                onValueChange={(value) => setVideoDeviceId(value)}
              >
                <SelectTrigger className="w-full bg-neutral-800 border-neutral-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue placeholder="Selecione uma câmera" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-800 border-neutral-600">
                  {videoInputDevices.map((device, index) => (
                    <SelectItem
                      key={device.deviceId}
                      value={device.deviceId}
                      className="text-white focus:bg-neutral-700 focus:text-white"
                    >
                      {device.label || `Câmera ${index + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'backgrounds' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Fundos virtuais</h3>

          {/* Preview do vídeo */}
          {videoTrack && (
            <div className="mb-6">
              <div className="relative w-full h-64 bg-neutral-900 rounded-lg overflow-hidden border border-neutral-700">
                <video
                  className="w-full h-full object-cover"
                  ref={(el) => {
                    if (el && videoTrack) {
                      videoTrack.attach(el);
                      // Não aplicar processadores nos previews dos modais para evitar conflitos
                    }
                  }}
                  data-preview-mode="true"
                />
              </div>
            </div>
          )}

          {/* Opções de fundo */}
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {/* Adicionar novo fundo */}
              <button
                type="button"
                className="relative group cursor-pointer p-3 rounded-lg border-2 border-dashed border-neutral-600 hover:border-neutral-500 hover:bg-neutral-800/40 transition-colors"
                onClick={handleOpenFileDialog}
              >
                <div className="aspect-video bg-neutral-800 rounded-md flex flex-col items-center justify-center gap-2 text-neutral-200">
                  {isUploadingCustomBackground ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-xs font-medium">Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      <span className="text-xs font-medium">Adicionar novo fundo</span>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </button>

              {/* Nenhum */}
              <button
                className={`relative group cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                  backgroundType === 'none'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-neutral-600 hover:border-neutral-500'
                }`}
                onClick={() => selectBackground('none')}
              >
                <div className="aspect-video bg-neutral-800 rounded-md flex items-center justify-center">
                  <div className="text-white text-xs font-medium">Nenhum</div>
                </div>
              </button>

              {/* Desfoque suave */}
              <button
                className={`relative group cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                  backgroundType === 'blur'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-neutral-600 hover:border-neutral-500'
                }`}
                onClick={() => selectBackground('blur')}
              >
                <div className="aspect-video bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/20 blur-sm"></div>
                  <div className="text-white text-xs font-medium relative z-10">Desfoque</div>
                </div>
              </button>

              {customBackgrounds.map((background) => (
                <div key={background.id} className="relative">
                  <button
                    type="button"
                    className={`relative group cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                      backgroundType === 'image' &&
                      virtualBackgroundImagePath === background.dataUrl
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-neutral-600 hover:border-neutral-500'
                    }`}
                    onClick={() => selectBackground('image', background.dataUrl)}
                  >
                    <div
                      className="aspect-video bg-cover bg-center rounded-md"
                      style={{ backgroundImage: `url(${background.dataUrl})` }}
                    >
                      <div className="w-full h-full bg-black/50 flex items-end justify-center p-1">
                        <span className="text-white text-xs font-medium">Personalizado</span>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="absolute top-1.5 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeCustomBackground(background.id);
                    }}
                    aria-label="Remover fundo personalizado"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            {customBackgroundError && (
              <p className="text-sm text-red-400">{customBackgroundError}</p>
            )}
            <p className="text-xs text-neutral-400">
              Formatos suportados: JPG e PNG. Tamanho máximo da imagem convertida: 5&nbsp;MB.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
